import * as THREE from 'three';
import { litMaterial } from '../core/materials';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

// Low-poly creatures/props assembled from primitives into two merged geometries:
// `body` (lit, vertex-coloured) and `glow` (unlit HDR colour that feeds bloom).

type Shape = 'sphere' | 'box' | 'cone' | 'cyl' | 'ico' | 'oct' | 'dodec' | 'tetra' | 'torus';
type V3 = [number, number, number];
// c: 'T' tint, 'D' dark tint, 'L' light tint, 'G' glow (goes to the glow mesh), or a hex colour.
interface Part {
  s: Shape;
  d: V3;
  p?: V3;
  r?: V3;
  c: string;
}

export interface ModelGeometry {
  body: THREE.BufferGeometry;
  glow: THREE.BufferGeometry | null;
}

const UNIT: Record<Shape, THREE.BufferGeometry> = {
  sphere: new THREE.SphereGeometry(0.5, 10, 7),
  box: new THREE.BoxGeometry(1, 1, 1),
  cone: new THREE.ConeGeometry(0.5, 1, 7),
  cyl: new THREE.CylinderGeometry(0.5, 0.5, 1, 8),
  ico: new THREE.IcosahedronGeometry(0.5, 0),
  oct: new THREE.OctahedronGeometry(0.5, 0),
  dodec: new THREE.DodecahedronGeometry(0.5, 0),
  tetra: new THREE.TetrahedronGeometry(0.5, 0),
  torus: new THREE.TorusGeometry(0.4, 0.1, 6, 18),
};

const tmpMatrix = new THREE.Matrix4();
const tmpQuat = new THREE.Quaternion();
const tmpEuler = new THREE.Euler();
const tmpColor = new THREE.Color();

function partGeometry(part: Part, color: THREE.Color): THREE.BufferGeometry {
  const base = UNIT[part.s];
  let g = base.index ? base.toNonIndexed() : base.clone();
  g.deleteAttribute('uv');
  const [rx, ry, rz] = part.r ?? [0, 0, 0];
  tmpQuat.setFromEuler(tmpEuler.set(rx, ry, rz));
  tmpMatrix.compose(new THREE.Vector3(...(part.p ?? [0, 0, 0])), tmpQuat, new THREE.Vector3(...part.d));
  g.applyMatrix4(tmpMatrix);
  const count = g.getAttribute('position').count;
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i += 1) {
    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  if (!g.getAttribute('normal')) g.computeVertexNormals();
  return g;
}

export function buildModel(parts: Part[], tint: string): ModelGeometry {
  const t = new THREE.Color(tint);
  const bodies: THREE.BufferGeometry[] = [];
  const glows: THREE.BufferGeometry[] = [];
  for (const part of parts) {
    if (part.c === 'G') {
      glows.push(partGeometry(part, tmpColor.setRGB(1, 1, 1)));
      continue;
    }
    if (part.c === 'T') tmpColor.copy(t);
    else if (part.c === 'D') tmpColor.copy(t).multiplyScalar(0.55);
    else if (part.c === 'L') tmpColor.copy(t).lerp(new THREE.Color('#ffffff'), 0.35);
    else tmpColor.set(part.c);
    bodies.push(partGeometry(part, tmpColor));
  }
  // Glow-only props (embers, shards) still need a body: use one degenerate, invisible triangle.
  const body = bodies.length ? mergeGeometries(bodies) : degenerate();
  const glow = glows.length ? mergeGeometries(glows) : null;
  for (const g of [...bodies, ...glows]) g.dispose();
  return { body, glow };
}

function degenerate(): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  for (const name of ['position', 'normal', 'color']) g.setAttribute(name, new THREE.BufferAttribute(new Float32Array(9), 3));
  return g;
}

const mirror = (part: Part): Part[] => {
  const p = part.p ?? [0, 0, 0];
  const r = part.r ?? [0, 0, 0];
  return [
    { ...part, p: [p[0], p[1], p[2]], r: [r[0], r[1], r[2]] },
    { ...part, p: [-p[0], p[1], p[2]], r: [r[0], -r[1], -r[2]] },
  ];
};

const eyes = (x: number, y: number, z: number, size: V3 = [0.12, 0.12, 0.06]): Part[] =>
  mirror({ s: 'sphere', d: size, p: [x, y, z], c: 'G' });

const ring = (n: number, radius: number, _y: number, make: (x: number, z: number, a: number) => Part): Part[] =>
  Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2;
    return make(Math.cos(a) * radius, Math.sin(a) * radius, a);
  });

const CREATURES: Record<string, Part[]> = {
  blob: [
    { s: 'sphere', d: [1, 0.78, 1], p: [0, 0.4, 0], c: 'T' },
    { s: 'sphere', d: [0.66, 0.45, 0.5], p: [0, 0.3, 0.26], c: 'L' },
    { s: 'cone', d: [0.18, 0.36, 0.18], p: [0.05, 0.88, -0.05], r: [0.2, 0, -0.25], c: 'D' },
    ...eyes(0.17, 0.55, 0.42, [0.16, 0.2, 0.1]),
  ],
  bat: [
    { s: 'sphere', d: [0.55, 0.5, 0.5], p: [0, 0.95, 0], c: 'T' },
    ...mirror({ s: 'box', d: [0.95, 0.04, 0.42], p: [0.62, 1.0, -0.05], r: [0, 0, 0.35], c: 'D' }),
    ...mirror({ s: 'cone', d: [0.12, 0.26, 0.12], p: [0.13, 1.24, 0.05], c: 'D' }),
    ...eyes(0.1, 0.98, 0.23, [0.1, 0.1, 0.06]),
  ],
  brute: [
    { s: 'box', d: [1.1, 0.9, 0.8], p: [0, 0.95, 0], c: 'T' },
    { s: 'box', d: [0.5, 0.45, 0.45], p: [0, 1.55, 0.15], c: 'T' },
    ...mirror({ s: 'box', d: [0.32, 0.95, 0.32], p: [0.75, 0.75, 0.1], c: 'D' }),
    ...mirror({ s: 'box', d: [0.36, 0.5, 0.36], p: [0.3, 0.25, 0], c: 'D' }),
    ...mirror({ s: 'sphere', d: [0.45, 0.32, 0.45], p: [0.6, 1.42, 0], c: 'L' }),
    ...eyes(0.12, 1.6, 0.38, [0.1, 0.08, 0.05]),
  ],
  caster: [
    { s: 'cone', d: [0.8, 1.2, 0.8], p: [0, 0.6, 0], c: 'T' },
    { s: 'sphere', d: [0.46, 0.46, 0.46], p: [0, 1.35, 0], c: 'D' },
    { s: 'cone', d: [0.58, 0.55, 0.58], p: [0, 1.58, -0.04], c: 'T' },
    { s: 'ico', d: [0.3, 0.3, 0.3], p: [0.38, 1.0, 0.35], c: 'G' },
    ...eyes(0.09, 1.37, 0.2, [0.08, 0.08, 0.05]),
  ],
  bomber: [
    { s: 'ico', d: [0.8, 0.8, 0.8], p: [0, 0.75, 0], c: 'T' },
    { s: 'oct', d: [0.9, 0.9, 0.9], p: [0, 0.75, 0], r: [0.3, 0.5, 0.2], c: 'G' },
    ...ring(5, 0.42, 0.75, (x, z, a) => ({ s: 'cone', d: [0.14, 0.38, 0.14], p: [x, 0.75, z], r: [Math.sin(a) * 1.57, 0, -Math.cos(a) * 1.57], c: 'D' })),
    { s: 'cone', d: [0.14, 0.38, 0.14], p: [0, 1.22, 0], c: 'D' },
  ],
  beast: [
    { s: 'box', d: [0.6, 0.55, 1.2], p: [0, 0.68, 0], c: 'T' },
    { s: 'box', d: [0.46, 0.42, 0.5], p: [0, 0.92, 0.7], c: 'T' },
    { s: 'box', d: [0.26, 0.2, 0.32], p: [0, 0.82, 1.02], c: 'L' },
    ...mirror({ s: 'cone', d: [0.13, 0.26, 0.13], p: [0.14, 1.2, 0.62], c: 'D' }),
    ...mirror({ s: 'box', d: [0.15, 0.48, 0.15], p: [0.2, 0.24, 0.42], c: 'D' }),
    ...mirror({ s: 'box', d: [0.15, 0.48, 0.15], p: [0.2, 0.24, -0.42], c: 'D' }),
    { s: 'cone', d: [0.15, 0.55, 0.15], p: [0, 0.82, -0.78], r: [-1.0, 0, 0], c: 'L' },
    ...eyes(0.12, 0.99, 0.96, [0.08, 0.06, 0.04]),
  ],
  wraith: [
    { s: 'cone', d: [0.72, 1.15, 0.72], p: [0, 0.85, 0], r: [Math.PI, 0, 0], c: 'T' },
    { s: 'sphere', d: [0.6, 0.6, 0.6], p: [0, 1.48, 0], c: 'T' },
    ...mirror({ s: 'cone', d: [0.15, 0.62, 0.15], p: [0.42, 1.12, 0.15], r: [0, 0, 0.85], c: 'D' }),
    ...eyes(0.12, 1.5, 0.27, [0.12, 0.16, 0.06]),
  ],
  golem: [
    { s: 'dodec', d: [1.2, 1.1, 1.0], p: [0, 1.0, 0], c: 'T' },
    { s: 'dodec', d: [0.6, 0.55, 0.55], p: [0, 1.75, 0.1], c: 'D' },
    ...mirror({ s: 'dodec', d: [0.55, 0.85, 0.55], p: [0.85, 0.9, 0.1], c: 'D' }),
    ...mirror({ s: 'dodec', d: [0.45, 0.5, 0.45], p: [0.35, 0.3, 0], c: 'D' }),
    { s: 'oct', d: [0.45, 0.6, 0.2], p: [0, 1.05, 0.48], c: 'G' },
    ...mirror({ s: 'box', d: [0.06, 0.5, 0.06], p: [0.85, 0.9, 0.38], c: 'G' }),
    ...eyes(0.13, 1.8, 0.36, [0.1, 0.08, 0.05]),
  ],
  eye: [
    { s: 'sphere', d: [0.95, 0.95, 0.95], p: [0, 1.05, 0], c: 'T' },
    { s: 'sphere', d: [0.48, 0.48, 0.2], p: [0, 1.05, 0.42], c: 'G' },
    { s: 'sphere', d: [0.2, 0.22, 0.1], p: [0, 1.05, 0.52], c: '#000000' },
    ...mirror({ s: 'cone', d: [0.12, 0.75, 0.12], p: [0.3, 0.45, 0], r: [0, 0, 0.4], c: 'D' }),
    { s: 'cone', d: [0.12, 0.75, 0.12], p: [0, 0.45, -0.3], r: [-0.4, 0, 0], c: 'D' },
  ],
  skeleton: [
    { s: 'sphere', d: [0.42, 0.42, 0.45], p: [0, 1.45, 0], c: 'T' },
    { s: 'box', d: [0.28, 0.1, 0.25], p: [0, 1.27, 0.06], c: 'T' },
    { s: 'cyl', d: [0.08, 0.7, 0.08], p: [0, 0.95, 0], c: 'T' },
    { s: 'box', d: [0.5, 0.06, 0.25], p: [0, 1.1, 0], c: 'T' },
    { s: 'box', d: [0.44, 0.06, 0.22], p: [0, 0.98, 0], c: 'T' },
    { s: 'box', d: [0.36, 0.06, 0.2], p: [0, 0.86, 0], c: 'T' },
    { s: 'box', d: [0.38, 0.12, 0.2], p: [0, 0.6, 0], c: 'D' },
    ...mirror({ s: 'cyl', d: [0.08, 0.62, 0.08], p: [0.14, 0.3, 0], c: 'T' }),
    ...mirror({ s: 'cyl', d: [0.07, 0.62, 0.07], p: [0.32, 0.95, 0.05], r: [0, 0, 0.2], c: 'T' }),
    ...eyes(0.09, 1.47, 0.19, [0.1, 0.1, 0.05]),
  ],
  brazier: [
    { s: 'cyl', d: [0.55, 0.15, 0.55], p: [0, 0.07, 0], c: 'D' },
    { s: 'cyl', d: [0.12, 0.85, 0.12], p: [0, 0.5, 0], c: 'T' },
    { s: 'cone', d: [0.65, 0.35, 0.65], p: [0, 1.02, 0], r: [Math.PI, 0, 0], c: 'T' },
    { s: 'cone', d: [0.35, 0.55, 0.35], p: [0, 1.4, 0], c: 'G' },
    { s: 'ico', d: [0.32, 0.32, 0.32], p: [0, 1.22, 0], c: 'G' },
  ],
  // --- Bosses ---
  treant: [
    { s: 'cyl', d: [1.4, 3.0, 1.4], p: [0, 1.5, 0], c: 'T' },
    { s: 'ico', d: [3.4, 2.4, 3.4], p: [0, 3.7, 0], c: '#2f5a2a' },
    { s: 'ico', d: [2.2, 1.8, 2.2], p: [0.9, 4.5, -0.3], c: '#3b6e33' },
    { s: 'ico', d: [1.8, 1.4, 1.8], p: [-1.0, 4.3, 0.4], c: '#2a4f25' },
    ...mirror({ s: 'cyl', d: [0.4, 2.1, 0.4], p: [1.3, 2.0, 0.3], r: [0, 0, 1.0], c: 'T' }),
    ...ring(4, 0.85, 0.3, (x, z, a) => ({ s: 'cone', d: [0.5, 1.1, 0.5], p: [x, 0.35, z], r: [Math.sin(a) * 1.1, 0, -Math.cos(a) * 1.1], c: 'D' })),
    ...ring(5, 1.6, 4.2, (x, z) => ({ s: 'sphere', d: [0.25, 0.25, 0.25], p: [x, 3.9, z], c: 'G' })),
    ...eyes(0.32, 2.45, 0.68, [0.3, 0.2, 0.12]),
    { s: 'box', d: [0.6, 0.12, 0.12], p: [0, 1.95, 0.68], c: 'G' },
  ],
  colossus: [
    { s: 'dodec', d: [2.6, 2.2, 2.0], p: [0, 2.4, 0], c: 'T' },
    { s: 'dodec', d: [1.1, 1.0, 1.0], p: [0, 3.9, 0.3], c: 'D' },
    ...mirror({ s: 'dodec', d: [1.2, 1.2, 1.2], p: [1.6, 3.2, 0], c: 'D' }),
    ...mirror({ s: 'dodec', d: [0.9, 1.9, 0.9], p: [1.85, 1.9, 0.2], c: 'T' }),
    ...mirror({ s: 'dodec', d: [1.0, 1.4, 1.0], p: [0.7, 0.7, 0], c: 'D' }),
    { s: 'oct', d: [0.9, 1.2, 0.45], p: [0, 2.4, 0.95], c: 'G' },
    ...mirror({ s: 'box', d: [0.12, 1.3, 0.12], p: [1.85, 1.9, 0.62], c: 'G' }),
    ...mirror({ s: 'box', d: [0.12, 0.9, 0.12], p: [0.7, 0.8, 0.5], c: 'G' }),
    ...eyes(0.25, 4.0, 0.78, [0.22, 0.14, 0.08]),
  ],
  matriarch: [
    { s: 'box', d: [1.6, 1.4, 3.0], p: [0, 1.6, 0], c: 'T' },
    { s: 'box', d: [1.1, 1.0, 1.2], p: [0, 2.3, 1.9], c: 'T' },
    { s: 'box', d: [0.6, 0.5, 0.7], p: [0, 2.05, 2.65], c: 'L' },
    { s: 'cone', d: [0.25, 0.9, 0.25], p: [0, 3.15, 1.8], c: 'G' },
    ...mirror({ s: 'cone', d: [0.22, 0.75, 0.22], p: [0.38, 3.0, 1.8], r: [0, 0, -0.3], c: 'G' }),
    ...mirror({ s: 'box', d: [0.45, 1.1, 0.45], p: [0.6, 0.55, 1.0], c: 'D' }),
    ...mirror({ s: 'box', d: [0.45, 1.1, 0.45], p: [0.6, 0.55, -1.0], c: 'D' }),
    { s: 'cone', d: [0.32, 1.1, 0.32], p: [0, 2.7, -0.9], c: 'G' },
    { s: 'cone', d: [0.32, 1.0, 0.32], p: [0, 2.65, 0.0], c: 'G' },
    { s: 'cone', d: [0.32, 0.9, 0.32], p: [0, 2.6, 0.8], c: 'G' },
    { s: 'cone', d: [0.4, 1.6, 0.4], p: [0, 1.9, -1.95], r: [-1.1, 0, 0], c: 'L' },
    ...eyes(0.32, 2.45, 2.5, [0.16, 0.12, 0.06]),
  ],
  lich: [
    { s: 'cone', d: [2.0, 3.0, 2.0], p: [0, 1.5, 0], c: 'T' },
    ...mirror({ s: 'sphere', d: [1.0, 0.6, 0.8], p: [0.8, 2.9, 0], c: 'D' }),
    { s: 'sphere', d: [0.9, 0.95, 0.9], p: [0, 3.45, 0], c: '#d9d2bf' },
    ...ring(5, 0.42, 3.95, (x, z) => ({ s: 'cone', d: [0.16, 0.55, 0.16], p: [x, 4.0, z], c: 'G' })),
    { s: 'cyl', d: [0.12, 3.6, 0.12], p: [1.25, 1.9, 0.45], c: '#3a2a1a' },
    { s: 'ico', d: [0.55, 0.55, 0.55], p: [1.25, 3.85, 0.45], c: 'G' },
    ...eyes(0.18, 3.48, 0.38, [0.16, 0.14, 0.06]),
  ],
  titan: [
    { s: 'box', d: [2.4, 2.0, 1.5], p: [0, 2.8, 0], c: 'T' },
    { s: 'box', d: [1.8, 1.0, 1.2], p: [0, 1.7, 0], c: 'D' },
    { s: 'box', d: [0.9, 0.9, 0.9], p: [0, 4.25, 0.1], c: 'T' },
    ...mirror({ s: 'box', d: [0.8, 2.3, 0.8], p: [1.7, 2.5, 0], c: 'D' }),
    ...mirror({ s: 'box', d: [0.8, 1.4, 0.8], p: [0.6, 0.7, 0], c: 'D' }),
    { s: 'box', d: [1.5, 0.12, 0.1], p: [0, 3.1, 0.78], c: 'G' },
    { s: 'box', d: [0.12, 1.1, 0.1], p: [0, 2.6, 0.78], c: 'G' },
    { s: 'cone', d: [0.32, 0.9, 0.32], p: [0, 5.0, 0.1], c: 'G' },
    ...mirror({ s: 'box', d: [0.1, 1.6, 0.1], p: [1.7, 2.5, 0.42], c: 'G' }),
    ...eyes(0.2, 4.3, 0.56, [0.18, 0.1, 0.06]),
  ],
  star: [
    { s: 'ico', d: [2.6, 2.6, 2.6], p: [0, 2.6, 0], c: 'T' },
    { s: 'oct', d: [3.0, 3.0, 3.0], p: [0, 2.6, 0], r: [0.5, 0.5, 0], c: 'G' },
    ...ring(8, 1.55, 2.6, (x, z, a) => ({ s: 'cone', d: [0.42, 1.6, 0.42], p: [x, 2.6, z], r: [Math.sin(a) * 1.57, 0, -Math.cos(a) * 1.57], c: 'D' })),
    { s: 'cone', d: [0.42, 1.6, 0.42], p: [0, 4.3, 0], c: 'D' },
    { s: 'sphere', d: [0.95, 0.95, 0.4], p: [0, 2.6, 1.3], c: 'G' },
    { s: 'sphere', d: [0.4, 0.5, 0.2], p: [0, 2.6, 1.48], c: '#000000' },
  ],
  // Azhdaha: a dune serpent-dragon, coils behind a rearing neck and a horned head.
  azhdaha: [
    { s: 'sphere', d: [1.7, 1.5, 1.7], p: [0, 0.8, -0.2], c: 'T' },
    { s: 'sphere', d: [1.5, 1.3, 1.5], p: [0.9, 0.7, -1.2], c: 'D' },
    { s: 'sphere', d: [1.3, 1.1, 1.3], p: [0.3, 0.6, -2.2], c: 'T' },
    { s: 'sphere', d: [1.05, 0.9, 1.05], p: [-0.7, 0.5, -2.8], c: 'D' },
    { s: 'sphere', d: [0.8, 0.7, 0.8], p: [-1.4, 0.4, -2.4], c: 'T' },
    { s: 'cone', d: [0.5, 1.2, 0.5], p: [-1.9, 0.35, -1.9], r: [0, 0, 1.2], c: 'D' },
    { s: 'sphere', d: [1.3, 1.6, 1.3], p: [0, 1.9, 0.4], c: 'T' },
    { s: 'sphere', d: [1.1, 1.2, 1.1], p: [0, 3.0, 0.8], c: 'T' },
    { s: 'box', d: [1.3, 0.9, 1.7], p: [0, 3.7, 1.3], c: 'T' },
    { s: 'box', d: [1.0, 0.35, 1.2], p: [0, 3.2, 1.7], c: 'D' },
    ...mirror({ s: 'cone', d: [0.25, 1.1, 0.25], p: [0.45, 4.4, 0.9], r: [-0.5, 0, -0.3], c: 'L' }),
    { s: 'cone', d: [0.3, 0.9, 0.3], p: [0, 2.7, 0.2], r: [-0.6, 0, 0], c: 'G' },
    { s: 'cone', d: [0.3, 0.8, 0.3], p: [0, 1.65, -0.3], r: [-0.4, 0, 0], c: 'G' },
    { s: 'cone', d: [0.28, 0.7, 0.28], p: [0.9, 1.4, -1.2], c: 'G' },
    { s: 'cone', d: [0.25, 0.6, 0.25], p: [0.3, 1.2, -2.2], c: 'G' },
    ...eyes(0.4, 3.85, 2.1, [0.2, 0.14, 0.08]),
  ],
  // Div-e Sepid, the White Div: a hulking horned giant with tusks.
  white_div: [
    { s: 'box', d: [2.6, 2.2, 1.6], p: [0, 2.7, 0], c: 'T' },
    { s: 'box', d: [2.0, 1.1, 1.3], p: [0, 1.55, 0], c: 'L' },
    { s: 'sphere', d: [1.2, 1.1, 1.1], p: [0, 4.2, 0.2], c: 'T' },
    ...mirror({ s: 'cone', d: [0.3, 1.3, 0.3], p: [0.55, 4.9, 0.1], r: [0.2, 0, -0.7], c: 'D' }),
    ...mirror({ s: 'cone', d: [0.14, 0.5, 0.14], p: [0.25, 3.85, 0.72], r: [0.3, 0, 0], c: '#f4ecd8' }),
    ...mirror({ s: 'box', d: [0.9, 2.6, 0.9], p: [1.85, 2.4, 0.1], r: [0, 0, 0.12], c: 'T' }),
    ...mirror({ s: 'sphere', d: [0.95, 0.85, 0.95], p: [2.0, 1.0, 0.25], c: 'D' }),
    ...mirror({ s: 'box', d: [0.9, 1.4, 0.9], p: [0.65, 0.7, 0], c: 'D' }),
    { s: 'box', d: [1.8, 0.12, 0.1], p: [0, 3.0, 0.82], c: 'G' },
    ...eyes(0.25, 4.3, 0.62, [0.2, 0.1, 0.06]),
  ],
  // Zahhak: a crowned king in a long robe, a serpent rising from each shoulder.
  zahhak: [
    { s: 'cone', d: [2.2, 3.0, 2.2], p: [0, 1.5, 0], c: 'T' },
    { s: 'cyl', d: [1.3, 0.25, 1.3], p: [0, 2.1, 0], c: 'G' },
    { s: 'box', d: [1.6, 1.0, 1.0], p: [0, 3.2, 0], c: 'T' },
    { s: 'sphere', d: [0.8, 0.85, 0.8], p: [0, 4.0, 0.05], c: '#d9b48f' },
    { s: 'cone', d: [0.5, 0.6, 0.4], p: [0, 3.55, 0.35], r: [Math.PI, 0, 0], c: 'D' },
    { s: 'torus', d: [1.1, 1.1, 1.1], p: [0, 4.45, 0], r: [Math.PI / 2, 0, 0], c: 'G' },
    ...ring(5, 0.4, 4.6, (x, z) => ({ s: 'cone', d: [0.14, 0.4, 0.14], p: [x, 4.7, z], c: 'G' })),
    ...mirror({ s: 'sphere', d: [0.5, 0.5, 0.5], p: [0.9, 3.7, -0.1], c: '#3d6b3a' }),
    ...mirror({ s: 'sphere', d: [0.45, 0.45, 0.45], p: [1.3, 4.3, 0], c: '#3d6b3a' }),
    ...mirror({ s: 'sphere', d: [0.4, 0.4, 0.4], p: [1.55, 4.9, 0.3], c: '#3d6b3a' }),
    ...mirror({ s: 'box', d: [0.45, 0.32, 0.65], p: [1.6, 5.25, 0.7], c: '#2f5a2c' }),
    ...mirror({ s: 'sphere', d: [0.09, 0.09, 0.05], p: [1.75, 5.35, 1.0], c: 'G' }),
    ...eyes(0.15, 4.05, 0.38, [0.1, 0.07, 0.05]),
  ],
};

export function creatureModel(model: string, tint: string): ModelGeometry {
  const parts = CREATURES[model];
  if (!parts) throw new Error(`Unknown model ${model}`);
  return buildModel(parts, tint);
}

// Props take the stage glow colour through 'G' parts.
const PROPS: Record<string, Part[]> = {
  tree: [
    { s: 'cyl', d: [0.35, 1.7, 0.35], p: [0, 0.85, 0], c: '#4a3524' },
    { s: 'ico', d: [1.9, 1.6, 1.9], p: [0, 2.2, 0], c: '#24502a' },
    { s: 'ico', d: [1.3, 1.2, 1.3], p: [0.3, 3.0, 0.1], c: '#2e6332' },
  ],
  rock: [
    { s: 'dodec', d: [1.3, 0.85, 1.05], p: [0, 0.32, 0], c: '#575c63' },
    { s: 'dodec', d: [0.6, 0.45, 0.55], p: [0.6, 0.18, 0.3], c: '#4a4e55' },
  ],
  grass: [
    { s: 'cone', d: [0.08, 0.42, 0.08], p: [0, 0.2, 0], r: [0.1, 0, 0.15], c: '#3c7a33' },
    { s: 'cone', d: [0.08, 0.36, 0.08], p: [0.1, 0.17, 0.05], r: [0, 0, -0.3], c: '#4d8f3a' },
    { s: 'cone', d: [0.08, 0.32, 0.08], p: [-0.08, 0.15, -0.04], r: [-0.2, 0, 0.25], c: '#356b2c' },
  ],
  spire: [
    { s: 'cone', d: [0.9, 3.2, 0.9], p: [0, 1.6, 0], c: '#2a1d19' },
    { s: 'box', d: [0.06, 1.8, 0.06], p: [0.18, 1.0, 0.3], c: 'G' },
  ],
  deadtree: [
    { s: 'cyl', d: [0.3, 2.4, 0.3], p: [0, 1.2, 0], c: '#2a1f1a' },
    { s: 'cyl', d: [0.14, 1.2, 0.14], p: [0.35, 2.0, 0], r: [0, 0, -0.9], c: '#2a1f1a' },
    { s: 'cyl', d: [0.12, 1.0, 0.12], p: [-0.3, 1.6, 0.1], r: [0.2, 0, 0.8], c: '#2a1f1a' },
  ],
  ember: [{ s: 'ico', d: [0.22, 0.12, 0.22], p: [0, 0.04, 0], c: 'G' }],
  pine: [
    { s: 'cyl', d: [0.3, 1.0, 0.3], p: [0, 0.5, 0], c: '#3b2a1e' },
    { s: 'cone', d: [1.8, 1.4, 1.8], p: [0, 1.4, 0], c: '#1f3d33' },
    { s: 'cone', d: [1.4, 1.2, 1.4], p: [0, 2.1, 0], c: '#24473b' },
    { s: 'cone', d: [0.9, 1.0, 0.9], p: [0, 2.8, 0], c: '#e8f1f8' },
  ],
  icespike: [
    { s: 'cone', d: [0.7, 2.3, 0.7], p: [0, 1.15, 0], c: '#a9d4ef' },
    { s: 'cone', d: [0.3, 1.6, 0.3], p: [0.35, 0.8, 0.1], r: [0, 0, -0.3], c: 'G' },
  ],
  tomb: [
    { s: 'box', d: [0.8, 1.0, 0.25], p: [0, 0.5, 0], c: '#4a4858' },
    { s: 'cyl', d: [0.8, 0.25, 0.8], p: [0, 1.0, 0], r: [Math.PI / 2, 0, 0], c: '#4a4858' },
    { s: 'box', d: [1.0, 0.15, 0.5], p: [0, 0.07, 0], c: '#36343f' },
  ],
  pillar: [
    { s: 'cyl', d: [0.9, 3.2, 0.9], p: [0, 1.6, 0], c: '#3a384a' },
    { s: 'box', d: [1.3, 0.3, 1.3], p: [0, 3.3, 0], c: '#2e2c3a' },
    { s: 'box', d: [1.3, 0.3, 1.3], p: [0, 0.15, 0], c: '#2e2c3a' },
    { s: 'box', d: [0.08, 1.4, 0.08], p: [0, 1.6, 0.46], c: 'G' },
  ],
  candle: [
    { s: 'cyl', d: [0.12, 0.38, 0.12], p: [0, 0.19, 0], c: '#e8e0c8' },
    { s: 'cone', d: [0.09, 0.18, 0.09], p: [0, 0.48, 0], c: 'G' },
    { s: 'cyl', d: [0.1, 0.24, 0.1], p: [0.2, 0.12, 0.1], c: '#e8e0c8' },
    { s: 'cone', d: [0.08, 0.15, 0.08], p: [0.2, 0.32, 0.1], c: 'G' },
  ],
  crag: [
    { s: 'dodec', d: [1.6, 2.4, 1.4], p: [0, 1.0, 0], c: '#4a5059' },
    { s: 'dodec', d: [0.9, 1.2, 0.8], p: [0.8, 0.5, 0.3], c: '#3f444c' },
  ],
  crystal: [
    { s: 'oct', d: [0.5, 1.8, 0.5], p: [0, 0.8, 0], c: 'G' },
    { s: 'oct', d: [0.35, 1.1, 0.35], p: [0.35, 0.5, 0.1], r: [0, 0, -0.4], c: 'G' },
    { s: 'oct', d: [0.3, 0.9, 0.3], p: [-0.3, 0.4, -0.1], r: [0, 0, 0.45], c: 'G' },
    { s: 'dodec', d: [0.9, 0.3, 0.9], p: [0, 0.1, 0], c: '#2a2d35' },
  ],
  obelisk: [
    { s: 'box', d: [0.8, 3.6, 0.8], p: [0, 1.8, 0], c: '#140b20' },
    { s: 'oct', d: [0.6, 0.9, 0.6], p: [0, 4.1, 0], c: 'G' },
    { s: 'box', d: [0.06, 2.6, 0.06], p: [0, 1.8, 0.41], c: 'G' },
  ],
  shard: [{ s: 'oct', d: [0.3, 0.7, 0.3], p: [0, 1.3, 0], c: 'G' }],
  kalut: [
    { s: 'box', d: [1.6, 2.6, 1.3], p: [0, 1.3, 0], r: [0, 0.3, 0], c: '#a5643a' },
    { s: 'box', d: [1.2, 1.4, 1.0], p: [0.1, 3.3, 0.05], r: [0, 0.6, 0], c: '#b9774a' },
    { s: 'cone', d: [0.9, 1.0, 0.8], p: [0.1, 4.5, 0], c: '#8f5530' },
  ],
  dune_rock: [
    { s: 'dodec', d: [1.4, 0.6, 1.1], p: [0, 0.25, 0], c: '#c08a55' },
    { s: 'dodec', d: [0.7, 0.4, 0.6], p: [0.7, 0.15, 0.3], c: '#a8743f' },
  ],
  salt: [
    { s: 'oct', d: [0.35, 0.7, 0.35], p: [0, 0.3, 0], c: '#f3ece0' },
    { s: 'oct', d: [0.25, 0.5, 0.25], p: [0.3, 0.2, 0.1], r: [0, 0, -0.4], c: '#e6ddcc' },
    { s: 'oct', d: [0.2, 0.4, 0.2], p: [-0.25, 0.18, -0.1], r: [0, 0, 0.4], c: '#ffffff' },
  ],
  hornbeam: [
    { s: 'cyl', d: [0.45, 2.2, 0.45], p: [0, 1.1, 0], c: '#3a2e22' },
    { s: 'ico', d: [2.4, 1.9, 2.4], p: [0, 2.8, 0], c: '#1b3a22' },
    { s: 'ico', d: [1.6, 1.4, 1.6], p: [0.5, 3.7, 0.2], c: '#21452a' },
    { s: 'ico', d: [1.4, 1.2, 1.4], p: [-0.6, 3.4, -0.3], c: '#173320' },
  ],
  boulder: [
    { s: 'dodec', d: [1.8, 1.4, 1.6], p: [0, 0.6, 0], c: '#4b5a46' },
    { s: 'dodec', d: [1.0, 0.3, 0.9], p: [0.1, 1.25, 0.1], c: '#3f6b35' },
  ],
  chain_pillar: [
    { s: 'cyl', d: [0.8, 3.0, 0.8], p: [0, 1.5, 0], c: '#3a3438' },
    { s: 'box', d: [1.2, 0.3, 1.2], p: [0, 3.1, 0], c: '#2b2629' },
    { s: 'torus', d: [0.5, 0.5, 0.5], p: [0.5, 2.2, 0], r: [0, 0, Math.PI / 2], c: '#5a5258' },
    { s: 'torus', d: [0.5, 0.5, 0.5], p: [0.75, 1.75, 0], r: [Math.PI / 2, 0, 0.3], c: '#5a5258' },
    { s: 'box', d: [0.08, 1.2, 0.08], p: [0, 1.5, 0.41], c: 'G' },
  ],
};

export function propModel(kind: string): ModelGeometry {
  const parts = PROPS[kind];
  if (!parts) throw new Error(`Unknown prop ${kind}`);
  return buildModel(parts, '#ffffff');
}

export const PROP_RADIUS: Record<string, number> = {
  tree: 0.5, rock: 0.75, spire: 0.55, deadtree: 0.3, pine: 0.45, icespike: 0.45, tomb: 0.5,
  pillar: 0.65, crag: 1.0, crystal: 0.5, obelisk: 0.6,
  kalut: 1.0, dune_rock: 0.8, hornbeam: 0.6, boulder: 1.0, chain_pillar: 0.55,
};

export function heroModel(robe: string, trim: string): { body: THREE.Mesh; orb: THREE.Mesh } {
  const { body } = buildModel(
    [
      { s: 'cone', d: [0.92, 1.25, 0.92], p: [0, 0.62, 0], c: robe },
      { s: 'cyl', d: [0.6, 0.1, 0.6], p: [0, 0.86, 0], c: trim },
      { s: 'sphere', d: [0.72, 0.42, 0.62], p: [0, 1.25, 0], c: robe },
      { s: 'sphere', d: [0.42, 0.42, 0.42], p: [0, 1.55, 0], c: '#f0d2b8' },
      { s: 'sphere', d: [0.07, 0.07, 0.04], p: [0.09, 1.57, 0.19], c: '#1a1a1a' },
      { s: 'sphere', d: [0.07, 0.07, 0.04], p: [-0.09, 1.57, 0.19], c: '#1a1a1a' },
      { s: 'cyl', d: [0.88, 0.06, 0.88], p: [0, 1.73, 0], c: robe },
      { s: 'cyl', d: [0.5, 0.08, 0.5], p: [0, 1.79, 0], c: trim },
      { s: 'cone', d: [0.52, 0.9, 0.52], p: [0, 2.18, -0.08], r: [-0.28, 0, 0], c: robe },
      { s: 'cyl', d: [0.07, 1.95, 0.07], p: [0.46, 0.97, 0.16], c: '#6b4a2e' },
      { s: 'torus', d: [0.35, 0.35, 0.35], p: [0.46, 1.95, 0.16], r: [Math.PI / 2, 0, 0], c: trim },
    ],
    '#ffffff',
  );
  const bodyMesh = new THREE.Mesh(body, litMaterial({ vertexColors: true, flatShading: true, roughness: 0.75 }));
  bodyMesh.castShadow = true;
  // The only non-instanced shadow caster: on three's shared depth material it would make the shadow pass switch
  // shaders between instanced and plain meshes (and rebuild the lookup) several times a frame.
  bodyMesh.customDepthMaterial = new THREE.MeshDepthMaterial();
  const orb = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.14, 1),
    new THREE.MeshBasicMaterial({ color: new THREE.Color(trim).multiplyScalar(3) }),
  );
  orb.position.set(0.46, 1.98, 0.16);
  return { body: bodyMesh, orb };
}
